import { DateTime } from '@piccolohealth/util';
import * as z from 'zod';

export const dateTimeSchema = z.custom<DateTime>(
  (val) => {
    const parsed = DateTime.fromISO(val as string);
    return parsed.isValid;
  },
  {
    message: 'Invalid date',
  },
);

// Zod combinator that will transform an empty string to null
export const stringToNull = <T extends z.ZodTypeAny>(zodType: T) => {
  return z.union([z.literal('').transform(() => null), zodType]).nullish();
};

export const isValidZodLiteralUnion = <T extends z.ZodLiteral>(
  literals: T[],
): literals is [T, T, ...T[]] => {
  return literals.length >= 2;
};

export const constructZodLiteralUnionType = <T extends z.core.util.Literal>(
  constArray: readonly T[],
) => {
  const literalsArray = constArray.map((literal) => z.literal(literal));
  if (!isValidZodLiteralUnion(literalsArray)) {
    throw new Error(
      'Literals passed do not meet the criteria for constructing a union schema, the minimum length is 2',
    );
  }
  return z.union(literalsArray);
};

export const zodHttpUrl = (message = 'Invalid URL') => {
  return z.url({
    protocol: /^https?$/,
    hostname: z.regexes.domain,
    error: message,
  });
};

export const zodNonEmptyString = (message = 'Required') => {
  return z.string(message).trim().min(1, message);
};

export const zodRequiredEmail = (message = 'Invalid email') => {
  return z.email(message).min(1);
};

export const zodPassword = z
  .string()
  .min(8, { message: 'Password must be at least 8 characters long' })
  .regex(/[A-Z]/, { message: 'Password must contain at least one uppercase letter' })
  .regex(/[!@#$%^&*(),.?":{}|<>]/, {
    message: 'Password must contain at least one special character',
  });

export interface ZodValidationResult {
  path: PropertyKey[];
  message: string;
  code: string;
}

export const validateSchema = <A>(
  schema: z.ZodType<A>,
  values: unknown,
  abortEarly: boolean = true,
): ZodValidationResult[] => {
  let issues: z.core.$ZodIssue[] = [];

  if (abortEarly) {
    try {
      schema.parse(values);
      return [];
    } catch (error) {
      if (error instanceof z.ZodError) {
        issues = error.issues.slice(0, 1);
      } else {
        throw error;
      }
    }
  } else {
    const result = schema.safeParse(values);
    if (!result.success) {
      issues = result.error.issues;
    }
  }

  return issues.map((issue) => ({
    path: issue.path,
    message: issue.message,
    code: issue.code,
  }));
};

export * as z from 'zod/v4';
